// src/hooks/useDiscoveryData.js
//
// All the Firestore fetching that Discover.jsx used to do inline,
// lifted into one reusable hook. Both the Discover/Gallery page and
// every new "See all" destination page call this once and get the
// same raw data + derived maps back — no duplicate fetches, no drift.

import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { normalizeCreator } from "../utils/homeSeedPreview";
import { fetchFeedDrops, isOpenFeedDrop } from "../services/dropsFeedService";
import { fetchCreators } from "../services/userQueryService";

export default function useDiscoveryData() {
  const { currentUser } = useAuth();

  const [creators, setCreators] = useState([]);
  const [moments, setMoments] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState([]);
  const [followedByNetwork, setFollowedByNetwork] = useState([]);
  const [purchasedDropIds, setPurchasedDropIds] = useState(new Set());
  const [subscribedCreatorIds, setSubscribedCreatorIds] = useState(new Set());
  const [currentUserCity, setCurrentUserCity] = useState("");

  // Session seed for fair rotation — stable for this browsing session
  // (so the Discover rails and every "See all" page agree with each
  // other while you're browsing), fresh on next visit. Never derived
  // from user behavior.
  const [sessionSeed] = useState(() => Math.floor(Math.random() * 1_000_000));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [creatorList, momentsSnap, dropsList] = await Promise.all([
          fetchCreators(100),
          getDocs(collection(db, "moments")),
          fetchFeedDrops(50),
        ]);
        if (!cancelled) {
          setCreators(creatorList);
          setMoments(momentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setDrops(dropsList);
        }
      } catch (err) {
        console.error("useDiscoveryData load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    getDocs(query(collection(db, "follows"), where("followerId", "==", currentUser.uid)))
      .then((snap) => setFollowingIds(snap.docs.map((d) => d.data().creatorId)))
      .catch(console.error);

    getDoc(doc(db, "users", currentUser.uid))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setPurchasedDropIds(new Set(data.purchasedDrops || []));
          setSubscribedCreatorIds(new Set(data.subscriptions || []));
          // Same field CreatorCard already reads (creator.city). If
          // your schema stores location elsewhere, adjust this line.
          setCurrentUserCity(data.city || "");
        }
      })
      .catch(console.error);
  }, [currentUser]);

  useEffect(() => {
    if (!followingIds.length || !creators.length) return;
    const load = async () => {
      try {
        const networkFollows = await Promise.all(
          followingIds.slice(0, 10).map((id) =>
            getDocs(query(collection(db, "follows"), where("followerId", "==", id)))
          )
        );
        const countMap = {};
        networkFollows.forEach((snap) => {
          snap.docs.forEach((d) => {
            const cid = d.data().creatorId;
            if (!followingIds.includes(cid) && cid !== currentUser?.uid) {
              countMap[cid] = (countMap[cid] || 0) + 1;
            }
          });
        });
        const sorted = Object.entries(countMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([id, count]) => {
            const c = creators.find((cr) => cr.id === id || cr.uid === id);
            return c ? { ...c, networkCount: count } : null;
          })
          .filter(Boolean);
        setFollowedByNetwork(sorted);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [followingIds, creators, currentUser]);

  const creatorById = useMemo(() => {
    const map = new Map();
    creators.forEach((c) => {
      map.set(c.id, normalizeCreator(c.id, c));
      if (c.uid) map.set(c.uid, normalizeCreator(c.id, c));
    });
    return map;
  }, [creators]);

  const dropsByCreator = useMemo(() => {
    const map = new Map();
    drops.forEach((d) => {
      const list = map.get(d.creatorId) || [];
      list.push(d);
      map.set(d.creatorId, list);
    });
    return map;
  }, [drops]);

  const networkIds = useMemo(
    () => new Set(followedByNetwork.map((c) => c.id)),
    [followedByNetwork]
  );

  const openDropCount = useMemo(
    () => drops.filter(isOpenFeedDrop).length,
    [drops]
  );

  return {
    // raw
    creators, moments, drops, loading,
    followingIds, followedByNetwork, networkIds,
    purchasedDropIds, subscribedCreatorIds,
    currentUserCity, sessionSeed,
    // derived
    creatorById, dropsByCreator, openDropCount,
  };
}